import { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Icon } from '~/global/components/icon/Icon';
import { cn } from '@/lib/utils';
import { useColor } from '~/global/hooks/useColor';
import { FullHeightWrapper } from '~/layout/components/full-height-wrapper/FullHeightWrapper';
import { logger } from '@/lib/logger';
import { usePurchases } from '~/purchases/contexts/purchases/Purchases';
import { CreditTopUp } from './CreditTopUp';
import {
  BILLING_PERIODS,
  BillingPeriod,
  SUBSCRIPTION_PLANS,
  SubscriptionPlanId,
  getSubscriptionPlan,
} from '~/purchases/defs/plans';

export interface SubscriptionScreenProps {
  /**
   * When provided, the close "X" always renders and calls this instead of
   * `router.back()`. Used when the screen is presented inside a modal (see
   * `PaywallModal`), where a router `back()` would navigate the underlying
   * stack rather than dismiss the overlay.
   */
  onRequestClose?: () => void;
}

/**
 * Tab-based subscription page built from the SSOT in `~/purchases/defs/plans`.
 * Plan tabs (Pro / AI Coach — Free excluded) + a Monthly/Yearly toggle drive a
 * feature list and a Subscribe button. Prices come live from the resolved
 * RevenueCat package; entitlement state flips Subscribe → "Current plan".
 */
export const SubscriptionScreen = ({
  onRequestClose,
}: SubscriptionScreenProps) => {
  const { t } = useTranslation('subscription');
  const router = useRouter();
  const {
    isReady,
    hasAiCoach,
    hasEntitlement,
    getPackage,
    purchasePackage,
    refresh,
    canManageSubscriptions,
    openManageSubscriptions,
  } = usePurchases();
  const checkColor = useColor('accent');

  // Pull the latest entitlements whenever the page opens, so an existing
  // subscription is reflected immediately (no manual "Restore" needed).
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedPlanId, setSelectedPlanId] = useState<SubscriptionPlanId>(
    SUBSCRIPTION_PLANS[0].id,
  );
  const [period, setPeriod] = useState<BillingPeriod>('yearly');
  const [purchasingId, setPurchasingId] = useState<string | null>(null);

  const plan = useMemo(
    () => getSubscriptionPlan(selectedPlanId),
    [selectedPlanId],
  );
  const pkg = getPackage(plan.packages[period]);
  // The user's actual plan is the highest tier whose entitlement is active — AI
  // Coach also grants `pro`, so checking this tab's entitlement alone would mark
  // Pro as "current" for an AI subscriber.
  const currentPlan = useMemo(
    () =>
      [...SUBSCRIPTION_PLANS]
        .sort((a, b) => b.tier - a.tier)
        .find((p) => hasEntitlement(p.entitlement)) ?? null,
    [hasEntitlement],
  );
  const isCurrent = currentPlan?.id === plan.id;
  const includedInHigher =
    !isCurrent && !!currentPlan && currentPlan.tier > plan.tier;

  // AI Coach subscribers don't need to pick a plan — they need to refill their
  // FYT wallet. Replace the plan tabs entirely with the credit top-up view (which
  // keeps its own "Cancel subscription" / "Restore" footer). Placed after all
  // hooks so the Rules of Hooks hold on both branches.
  if (hasAiCoach) {
    return <CreditTopUp onRequestClose={onRequestClose} />;
  }

  const handleSubscribe = async () => {
    if (!pkg) {
      return;
    }
    setPurchasingId(pkg.identifier);
    try {
      await purchasePackage(pkg);
    } catch (e) {
      logger.error(e, '[SubscriptionScreen] purchase failed');
    } finally {
      setPurchasingId(null);
    }
  };

  // In modal (embedded) presentation the header + close button are pinned above
  // a scrollable body; as a full-screen route they scroll with the content.
  const embedded = !!onRequestClose;

  const closeButton = (onRequestClose || router.canGoBack()) && (
    <Pressable
      testID="paywall-close"
      onPress={() => (onRequestClose ? onRequestClose() : router.back())}
      hitSlop={8}
      className="p-1 active:opacity-70"
      accessibilityRole="button"
    >
      <Icon name="X" size={24} />
    </Pressable>
  );

  const header = (
    <View className="flex-row items-start justify-between gap-x-2">
      <View className="flex-1 gap-y-1">
        <Text className="text-2xl font-bold">{t('title')}</Text>
        <Text className="text-sm text-text-secondary">{t('subtitle')}</Text>
      </View>
      {closeButton}
    </View>
  );

  const scroll = (
    <ScrollView
      className="flex-1"
      contentContainerClassName={cn(
        'items-center',
        embedded ? 'px-6 pb-6 pt-4 md:px-4' : 'px-6 py-4 md:px-4 md:pt-20',
      )}
      showsVerticalScrollIndicator={false}
    >
      <View className="gap-y-6 w-full max-w-lg pb-10">
        {!embedded && header}

        {/* Plan tabs */}
        <View className="flex-row rounded-full bg-surface-elevated p-1">
          {SUBSCRIPTION_PLANS.map((p) => {
            const active = p.id === selectedPlanId;
            return (
              <Pressable
                key={p.id}
                onPress={() => setSelectedPlanId(p.id)}
                className={cn(
                  'flex-1 items-center justify-center rounded-full py-2',
                  active && 'bg-accent',
                )}
              >
                <Text
                  className={cn(
                    'text-sm font-semibold',
                    active ? 'text-accent-foreground' : 'text-text-secondary',
                  )}
                >
                  {t(`plans.${p.id}.name`)}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {/* Billing period toggle */}
        <View className="flex-row self-center rounded-full bg-surface-elevated p-1">
          {BILLING_PERIODS.map((per) => {
            const active = per === period;
            return (
              <Pressable
                key={per}
                onPress={() => setPeriod(per)}
                className={cn(
                  'flex-row items-center gap-x-2 rounded-full px-5 py-2',
                  active && 'bg-surface',
                )}
              >
                <Text
                  className={cn(
                    'text-sm font-semibold',
                    active ? 'text-foreground' : 'text-text-secondary',
                  )}
                >
                  {t(`period.${per}`)}
                </Text>
                {per === 'yearly' && (
                  <View className="rounded-full bg-accent px-2 py-0.5">
                    <Text className="text-xs font-bold text-accent-foreground">
                      {t('saveBadge')}
                    </Text>
                  </View>
                )}
              </Pressable>
            );
          })}
        </View>

        {/* Plan body */}
        <View className="gap-y-5">
          <View className="gap-y-1">
            <Text className="text-base font-semibold">
              {t(`plans.${plan.id}.tagline`)}
            </Text>
            <View className="flex-row items-end gap-x-2">
              <Text className="text-3xl font-extrabold">
                {pkg?.priceString ?? '—'}
              </Text>
              <Text className="pb-1 text-sm text-text-secondary">
                {t(`period.${period}Unit`)}
              </Text>
            </View>
          </View>

          {/* Feature list */}
          <View className="gap-y-3">
            {plan.featureKeys.map((key) => (
              <View key={key} className="flex-row items-start gap-x-3">
                <View className="pt-0.5">
                  <Icon name="Check" size={16} color={checkColor} />
                </View>
                <Text className="flex-1 text-sm">
                  {t(`plans.${plan.id}.features.${key}`)}
                </Text>
              </View>
            ))}
          </View>

          {/* Subscribe / current plan / included in a higher plan */}
          {isCurrent ? (
            <Button variant="secondary" disabled>
              <Text>{t('currentPlan')}</Text>
            </Button>
          ) : includedInHigher ? (
            <Button variant="secondary" disabled>
              <Text>{t('includedInPlan')}</Text>
            </Button>
          ) : (
            <Button
              onPress={handleSubscribe}
              disabled={!isReady || !pkg}
              loading={purchasingId === pkg?.identifier}
            >
              <Text>{t('subscribe')}</Text>
            </Button>
          )}

          {!isReady && (
            <Text className="text-center text-xs text-text-secondary">
              {t('loading')}
            </Text>
          )}
          {isReady && !pkg && (
            <Text className="text-center text-xs text-text-secondary">
              {t('unavailable')}
            </Text>
          )}
        </View>

        {/* Manage / cancel + restore */}
        <View className="gap-y-1">
          {currentPlan && canManageSubscriptions && (
            <Button
              variant="link"
              size="sm"
              onPress={() => void openManageSubscriptions()}
              className="self-center"
            >
              <Text className="text-sm text-text-secondary">{t('cancel')}</Text>
            </Button>
          )}
          <RestoreButton label={t('restore')} />
        </View>
      </View>
    </ScrollView>
  );

  if (embedded) {
    return (
      // `paywall-modal`: stable e2e handle for the paywall shown when a free user taps a
      // gated action (custom foods/exercises, drained AI wallet, …). See apps/app/.maestro.
      <View testID="paywall-modal" className="flex-1 bg-background">
        <View className="items-center border-b border-border px-6 pb-4 pt-5 md:px-4">
          <View className="w-full max-w-lg">{header}</View>
        </View>
        {scroll}
      </View>
    );
  }

  return <FullHeightWrapper>{scroll}</FullHeightWrapper>;
};

const RestoreButton = ({ label }: { label: string }) => {
  const { restorePurchases } = usePurchases();
  const [busy, setBusy] = useState(false);
  const handleRestore = async () => {
    setBusy(true);
    try {
      await restorePurchases();
    } catch (e) {
      logger.error(e, '[SubscriptionScreen] restore failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="link"
      size="sm"
      onPress={handleRestore}
      loading={busy}
      className="self-center"
    >
      <Text className="text-sm text-text-secondary">{label}</Text>
    </Button>
  );
};
